import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import SimpleSpinner from "../../../components/loader";
import "./vacancy-rate.css";
import TimeRangeDropdown from "../../time-range-filter/time-range-dropdown";
import EmptyState from "../../emptystate";
import { GetImages } from "../../../utils/GetImages";

/* ------------------------------------------------------------------ */
/*  Custom Tooltip                                                    */
/* ------------------------------------------------------------------ */
const CustomTooltip = ({
  active,
  payload,
  label
}: {
  active?: boolean;
  payload?: any[];
  label?: string;
}) => {
  if (active && payload && payload.length > 0) {
    return (
      <div className="toolTipContainer2">
        <p className="sub_text fw-bold">{label}</p>
        <div className="d-flex flex-column vacancyDataContainer">
          {payload.map((entry, index) => (
            <div key={index} className="d-flex justify-content-between gap-4 align-items-center">
              <div className="d-flex align-items-center gap-2">
                <div 
                  style={{ 
                    width: '8px', 
                    height: '8px', 
                    borderRadius: '50%', 
                    backgroundColor: entry.color 
                  }} 
                />
                <p className="sub_text grey_subtext1" style={{color:entry.color}}>{entry.name}:</p>
              </div>
              <p className="normal_text black" style={{color:entry.color}}>{entry.value}</p>
            </div>
          ))}
        </div>
      </div>
    );
  }
  return null;
};

interface VacancyRateProps {
  timeRanges: string[];
  selectedTimeRange: string;
  onTimeRangeChange: (range: string) => void;
  isLoading?: boolean;
  data: Array<{
    month: string;
    occupied: number;
    unoccupied: number;
  }>;
}

export default function VacancyRate({
  timeRanges,
  selectedTimeRange,
  onTimeRangeChange,
  isLoading = false,
  data = [],
}: VacancyRateProps) {
  const loading = isLoading;

  return (
    <div className="vacancy-container">
      <div className="vacancy-header">
        <h2 className="vacancy-title">Vacancy Rate</h2>
        <TimeRangeDropdown
          timeRanges={timeRanges}
          defaultTimeRange={selectedTimeRange}
          onTimeRangeChange={onTimeRangeChange}
        />
      </div>

      <div className="vacancy-chart-wrapper">
        {loading ? (
          <div className="chart-loading">
            <SimpleSpinner myClassName="Icon40" />
          </div>
        ) : data.length === 0 ? (
          <EmptyState
            icon={GetImages.emptyStateData}
            className="w-100"
            isBorder={false}
            heading="No Data found"
            isButton={false}
          />
        ) : (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={data} margin={{ top: 20, right: 30, left: 0, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--Grey-Divider)" vertical={true} />
              <XAxis
                dataKey="month"
                stroke="var(--Grey-Subtext2)"
                style={{ fontSize: "14px" }}
                axisLine={{ stroke: "var(--Grey-Divider)" }}
                tickLine={false}
              />
              <YAxis
                stroke="var(--Grey-Subtext2)"
                style={{ fontSize: "14px" }}
                axisLine={{ stroke: "var(--Grey-Divider)" }}
                tickLine={false}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                verticalAlign="bottom"
                height={36}
                wrapperStyle={{ paddingTop: "20px" }}
                iconType="circle"
                formatter={(value) => <span style={{ color: "var(--Grey-Heading)" }}>{value}</span>}
              />
              <Bar dataKey="occupied" stackId="a" fill="#0066ff" name="Occupied" />
              <Bar dataKey="unoccupied" stackId="a" fill="#ff6b35" name="Unoccupied" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}